import { supabase } from '../supabaseClient';
import type {
  AdminCrossCurricularCatalog,
  AdminCrossCurricularContent,
  AdminCrossCurricularTopic,
} from '../types/adminCrossCurricular';
import {
  getAdminCrossCurricularCatalogInflight,
  invalidateAdminCrossCurricularCatalogCache,
  loadAdminCrossCurricularCatalogCache,
  saveAdminCrossCurricularCatalogCache,
  setAdminCrossCurricularCatalogInflight,
} from '../utils/adminCrossCurricularCatalogCache';

const TOPIC_SELECT = 'id, name, sort_order, created_at, updated_at';
const CONTENT_SELECT = 'id, topic_id, body, sort_order, created_at, updated_at';

function mapTopic(row: Record<string, unknown>): AdminCrossCurricularTopic {
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? '').trim(),
    sort_order: Number(row.sort_order) || 0,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapContent(row: Record<string, unknown>): AdminCrossCurricularContent {
  return {
    id: String(row.id ?? ''),
    topic_id: String(row.topic_id ?? ''),
    body: String(row.body ?? '').trim(),
    sort_order: Number(row.sort_order) || 0,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

export async function listAdminCrossCurricularCatalog(): Promise<AdminCrossCurricularCatalog> {
  const [topicsRes, contentsRes] = await Promise.all([
    supabase
      .from('admin_cross_curricular_topics')
      .select(TOPIC_SELECT)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('admin_cross_curricular_contents')
      .select(CONTENT_SELECT)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
  ]);

  if (topicsRes.error) throw new Error(topicsRes.error.message);
  if (contentsRes.error) throw new Error(contentsRes.error.message);

  const catalog = {
    topics: (topicsRes.data ?? []).map((r) => mapTopic(r as Record<string, unknown>)),
    contents: (contentsRes.data ?? []).map((r) => mapContent(r as Record<string, unknown>)),
  };
  saveAdminCrossCurricularCatalogCache(catalog);
  return catalog;
}

/**
 * 캐시 우선으로 플랫폼 범교과 카탈로그 반환.
 * - 메모리/sessionStorage hit → DB 호출 없음
 * - miss → DB 조회 후 캐시 기록
 */
export async function resolveAdminCrossCurricularCatalog(options?: {
  forceRefresh?: boolean;
}): Promise<AdminCrossCurricularCatalog> {
  if (!options?.forceRefresh) {
    const cached = loadAdminCrossCurricularCatalogCache();
    if (cached.found) return cached.catalog;
    const pending = getAdminCrossCurricularCatalogInflight();
    if (pending) return pending;
  }

  const request = listAdminCrossCurricularCatalog().finally(() => {
    if (getAdminCrossCurricularCatalogInflight() === request) {
      setAdminCrossCurricularCatalogInflight(null);
    }
  });
  setAdminCrossCurricularCatalogInflight(request);
  return request;
}

export async function createAdminCrossCurricularTopic(name: string): Promise<AdminCrossCurricularTopic> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('주제어를 입력해 주세요.');

  const { data: maxRow, error: maxErr } = await supabase
    .from('admin_cross_curricular_topics')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (maxErr) throw new Error(maxErr.message);
  const sort_order = (Number(maxRow?.sort_order) || 0) + 1;

  const { data, error } = await supabase
    .from('admin_cross_curricular_topics')
    .insert({ name: trimmed, sort_order })
    .select(TOPIC_SELECT)
    .single();
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
  return mapTopic(data as Record<string, unknown>);
}

export async function updateAdminCrossCurricularTopic(
  id: string,
  patch: { name?: string; sort_order?: number }
): Promise<AdminCrossCurricularTopic> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) {
    const trimmed = patch.name.trim();
    if (!trimmed) throw new Error('주제어를 입력해 주세요.');
    payload.name = trimmed;
  }
  if (patch.sort_order !== undefined) payload.sort_order = patch.sort_order;

  const { data, error } = await supabase
    .from('admin_cross_curricular_topics')
    .update(payload)
    .eq('id', id)
    .select(TOPIC_SELECT)
    .single();
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
  return mapTopic(data as Record<string, unknown>);
}

export async function deleteAdminCrossCurricularTopic(id: string): Promise<void> {
  const { error } = await supabase.from('admin_cross_curricular_topics').delete().eq('id', id);
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
}

/** 주제어 목록 순서(sort_order) 일괄 저장 */
export async function reorderAdminCrossCurricularTopics(orderedTopicIds: string[]): Promise<void> {
  const ids = orderedTopicIds.map((id) => id.trim()).filter(Boolean);
  if (!ids.length) return;

  const { data, error } = await supabase
    .from('admin_cross_curricular_topics')
    .select('id, sort_order');
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as Array<{ id: string; sort_order: number }>;
  const byId = new Map(rows.map((row) => [row.id, row]));
  const allIdsInCurrentOrder = [...rows]
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))
    .map((row) => row.id);
  const used = new Set(ids);
  const mergedIds = [...ids, ...allIdsInCurrentOrder.filter((id) => !used.has(id))];

  const updates: Array<{ id: string; orderIndex: number; currentOrderIndex: number }> = [];
  mergedIds.forEach((id, idx) => {
    const row = byId.get(id);
    if (!row || row.sort_order === idx) return;
    updates.push({ id, orderIndex: idx, currentOrderIndex: row.sort_order });
  });
  if (!updates.length) {
    invalidateAdminCrossCurricularCatalogCache();
    return;
  }

  const now = new Date().toISOString();
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_cross_curricular_topics')
      .update({ sort_order: update.currentOrderIndex + 1000, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_cross_curricular_topics')
      .update({ sort_order: update.orderIndex, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  invalidateAdminCrossCurricularCatalogCache();
}

export async function createAdminCrossCurricularContent(input: {
  topic_id: string;
  body: string;
}): Promise<AdminCrossCurricularContent> {
  const topic_id = input.topic_id.trim();
  const body = input.body.trim();
  if (!topic_id) throw new Error('주제어를 선택해 주세요.');
  if (!body) throw new Error('학습내용을 입력해 주세요.');

  const { data: maxRow, error: maxErr } = await supabase
    .from('admin_cross_curricular_contents')
    .select('sort_order')
    .eq('topic_id', topic_id)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (maxErr) throw new Error(maxErr.message);
  const sort_order = (Number(maxRow?.sort_order) || 0) + 1;

  const { data, error } = await supabase
    .from('admin_cross_curricular_contents')
    .insert({ topic_id, body, sort_order })
    .select(CONTENT_SELECT)
    .single();
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
  return mapContent(data as Record<string, unknown>);
}

export async function updateAdminCrossCurricularContent(
  id: string,
  patch: { topic_id?: string; body?: string; sort_order?: number }
): Promise<AdminCrossCurricularContent> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.topic_id !== undefined) {
    const topic_id = patch.topic_id.trim();
    if (!topic_id) throw new Error('주제어를 선택해 주세요.');
    payload.topic_id = topic_id;
  }
  if (patch.body !== undefined) {
    const body = patch.body.trim();
    if (!body) throw new Error('학습내용을 입력해 주세요.');
    payload.body = body;
  }
  if (patch.sort_order !== undefined) payload.sort_order = patch.sort_order;

  const { data, error } = await supabase
    .from('admin_cross_curricular_contents')
    .update(payload)
    .eq('id', id)
    .select(CONTENT_SELECT)
    .single();
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
  return mapContent(data as Record<string, unknown>);
}

export async function deleteAdminCrossCurricularContent(id: string): Promise<void> {
  const { error } = await supabase.from('admin_cross_curricular_contents').delete().eq('id', id);
  if (error) throw new Error(error.message);
  invalidateAdminCrossCurricularCatalogCache();
}

/** 주제어별 학습내용 일괄 치환(빈 body 행 제외). */
export async function replaceAdminCrossCurricularContentsForTopic(
  topicId: string,
  rows: Array<{ id?: string | null; body: string }>
): Promise<AdminCrossCurricularContent[]> {
  const tid = topicId.trim();
  if (!tid) throw new Error('주제어를 선택해 주세요.');

  const kept = rows
    .map((r, idx) => ({
      id: typeof r.id === 'string' && r.id.trim() ? r.id.trim() : null,
      body: (r.body ?? '').trim(),
      sort_order: idx,
    }))
    .filter((r) => r.body.length > 0);

  const { data: existing, error: listErr } = await supabase
    .from('admin_cross_curricular_contents')
    .select('id')
    .eq('topic_id', tid);
  if (listErr) throw new Error(listErr.message);

  const keepIds = new Set(kept.map((r) => r.id).filter((id): id is string => !!id));
  const toDelete = (existing ?? [])
    .map((r) => String((r as { id?: string }).id ?? ''))
    .filter((id) => id && !keepIds.has(id));

  if (toDelete.length) {
    const { error: delErr } = await supabase.from('admin_cross_curricular_contents').delete().in('id', toDelete);
    if (delErr) throw new Error(delErr.message);
  }

  const out: AdminCrossCurricularContent[] = [];
  for (const row of kept) {
    if (row.id) {
      const { data, error } = await supabase
        .from('admin_cross_curricular_contents')
        .update({ body: row.body, sort_order: row.sort_order, updated_at: new Date().toISOString() })
        .eq('id', row.id)
        .eq('topic_id', tid)
        .select(CONTENT_SELECT)
        .single();
      if (error) throw new Error(error.message);
      out.push(mapContent(data as Record<string, unknown>));
    } else {
      const { data, error } = await supabase
        .from('admin_cross_curricular_contents')
        .insert({ topic_id: tid, body: row.body, sort_order: row.sort_order })
        .select(CONTENT_SELECT)
        .single();
      if (error) throw new Error(error.message);
      out.push(mapContent(data as Record<string, unknown>));
    }
  }
  invalidateAdminCrossCurricularCatalogCache();
  return out;
}
